import { Component, inject } from '@angular/core';

import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent, MatDialogModule, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { IInstructions } from '../../automation/interfaces/i-instructions';
import { AutomationStateService } from '../services/automation-state.service';
import { IDocument } from '../../../interfaces/i-document';
import { ChatDocumentService } from '../services/chat-document.service';
import { MatListModule } from '@angular/material/list';
import { DocumentManagerComponent } from '../document-manager/document-manager.component';
import { ChatSessionService } from '../services/chat-session.service';
import { AttachmentDocComponent } from "../../../shared/attachment-doc/attachment-doc.component";
import { KENDO_BUTTONS } from "@progress/kendo-angular-buttons";
import { minusOutlineIcon } from "@progress/kendo-svg-icons";
import { MatTooltipModule } from "@angular/material/tooltip";
import { AutomationExecutionService } from "../services/automation-execution.service";
import { A11yModule } from '@angular/cdk/a11y';

@Component({
  selector: 'app-instruction-user-file-upload',
  standalone: true,
  imports: [
    MatDialogContent,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatTooltipModule,
    A11yModule,
    AttachmentDocComponent,
    KENDO_BUTTONS
],
  templateUrl: './instruction-user-file-upload.component.html',
  styleUrls: ['./instruction-user-file-upload.component.scss']
})
export class InstructionUserFileUploadComponent {
  instruction!: IInstructions;
  label = '';
  prompt = '';
  files: IDocument[] = [];
  cancelButtonText = 'Cancel';

  protected readonly minusOutlineIcon = minusOutlineIcon;

  #automationStateService = inject(AutomationStateService);
  #automationExecutionService = inject(AutomationExecutionService);
  #dialogRef = inject(MatDialogRef<InstructionUserFileUploadComponent>);
  #chatDocumentService = inject(ChatDocumentService);
  #chatSessionService = inject(ChatSessionService);
  #dialog = inject(MatDialog);

  readonly data = inject<{ instruction: IInstructions }>(MAT_DIALOG_DATA);

  constructor() {
    this.instruction = this.data.instruction;
    this.label = this.instruction.details.fileUploadLabel ?? '';
    this.prompt = this.instruction.details.prompt ?? '';
    this.files = this.#chatDocumentService.selectedDocuments();
    this.cancelButtonText = this.#automationStateService.isWorkflowInProgress() ? 'Cancel Workflow' : 'Cancel';
  }

  openDocumentManager(): void {
    // Open the document manager dialog
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.width = '100vw';
    dialogConfig.height = '100vh';
    dialogConfig.maxWidth = '100vw';
    dialogConfig.maxHeight = '100vh';

    // Open the document manager dialog
    const documentManagerDialogRef = this.#dialog.open(DocumentManagerComponent, dialogConfig);

    // When document manager closes, refresh our file list
    documentManagerDialogRef.afterClosed().subscribe(() => {
      this.files = this.#chatDocumentService.selectedDocuments();

      if (!this.#automationStateService.isWorkflowInProgress()) {
        // If not in a workflow, update the session input data documents
        const documents = this.#chatDocumentService.selectedDocuments();
        this.#chatSessionService.updateSessionInputDataDocuments(documents);
      }
    });
  }

  removeFile(index: number): void {
    const selectedDocuments = this.#chatDocumentService.selectedDocuments();
    selectedDocuments.splice(index, 1);
    this.#chatDocumentService.selectedDocuments.set(selectedDocuments);
    this.files = selectedDocuments;
  }

  submit(): void {
    // If files are attached, return success to close the dialog
    if (this.files.length > 0) {
      this.#dialogRef.close('success');
    }
  }

  cancel(): void {
    // If cancelling and in a workflow, mark the workflow as cancelled
    if (this.#automationStateService.workflowExecutionState()) {
      this.#automationExecutionService.endWorkflowExecution(true);
    }
    this.#dialogRef.close();
  }
}
